import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { DeliveryModes } from "../../../types";
import {
  getConsolidatedSupplierGroupKey,
  getCustomerSupplierGroupKey,
  stripConsolidatedSelectionGroups,
} from "./selectionGroupUtils";

export {
  getActiveSelectionGroups,
  getConsolidatedSupplierGroupKey,
  getCustomerSupplierGroupKey,
  isConsolidatedSelectionGroup,
  stripConsolidatedSelectionGroups,
} from "./selectionGroupUtils";

export interface GroupSelection {
  customerId: string;
  customerName: string;
  supplierId: string;
  supplierName: string;
  selectedOrders: string[];
  orderIdMap: Record<string, string>;
  orderDataMap: Record<string, any>;
  porterQuote: any | null;
  porterQuoteLoading: boolean;
  selectedCustomerSupplier: any | null;
  lastQuoteOrderHash?: string;
  orderDeliveryModes: Record<string, DeliveryModes>;
  isConsolidatedGroup?: boolean;
  consigneeNames?: Record<string, string>;
}

interface R2DOrdersState {
  selectedOrders: string[];
  selectionsByGroup: Record<string, GroupSelection>;
  allowMultiCustomerShipment: boolean;

  setAllowMultiCustomerShipment: (enabled: boolean) => void;

  setSelectedOrders: (orders: string[]) => void;
  addSelectedOrder: (
    orderNo: string,
    customerId: string,
    customerName: string,
    supplierId?: string,
    supplierName?: string,
    deliveryMode?: DeliveryModes,
    orderId?: string,
    orderData?: any
  ) => void;
  removeSelectedOrder: (
    orderNo: string,
    customerId: string,
    customerName: string,
    supplierId?: string,
    supplierName?: string
  ) => void;
  clearGroup: (groupKey: string) => void;
  clearAllSelections: () => void;
  updateGroupQuote: (
    groupKey: string,
    updates: Partial<GroupSelection>
  ) => void;
  setGroupLoading: (groupKey: string, loading: boolean) => void;
}

const buildEmptyGroup = (
  customerId: string,
  customerName: string,
  supplierId: string,
  supplierName: string,
  isConsolidatedGroup = false
): GroupSelection => ({
  customerId,
  customerName,
  supplierId,
  supplierName,
  selectedOrders: [],
  orderIdMap: {},
  orderDataMap: {},
  porterQuote: null,
  porterQuoteLoading: false,
  selectedCustomerSupplier: null,
  lastQuoteOrderHash: undefined,
  orderDeliveryModes: {},
  isConsolidatedGroup,
  consigneeNames: {},
});

export const useR2DOrdersStore = create<R2DOrdersState>()(
  persist(
    (set) => ({
      selectedOrders: [],
      selectionsByGroup: {},
      allowMultiCustomerShipment: false,

      setAllowMultiCustomerShipment: (enabled) =>
        set((state) => {
          if (enabled) {
            return { allowMultiCustomerShipment: true };
          }

          return {
            allowMultiCustomerShipment: false,
            selectionsByGroup: stripConsolidatedSelectionGroups(
              state.selectionsByGroup,
            ),
          };
        }),

      setSelectedOrders: (orders) => set({ selectedOrders: orders }),

      addSelectedOrder: (
        orderNo,
        customerId,
        customerName,
        supplierId,
        supplierName,
        deliveryMode,
        orderId,
        orderData
      ) => {
        const safeSupplierId = supplierId || "no-supplier";
        const safeSupplierName = supplierName || "";
        const groupKey = getCustomerSupplierGroupKey(
          customerId,
          safeSupplierId
        );

        set((state) => {
          const existing =
            state.selectionsByGroup[groupKey] ||
            buildEmptyGroup(
              customerId,
              customerName,
              safeSupplierId,
              safeSupplierName
            );

          const existingSet = new Set(existing.selectedOrders);
          existingSet.add(orderNo);
          const nextSelectedOrders = Array.from(existingSet);

          const nextOrderDeliveryModes = {
            ...(existing.orderDeliveryModes || {}),
          } as Record<string, DeliveryModes>;
          if (deliveryMode !== undefined) {
            nextOrderDeliveryModes[orderNo] = deliveryMode;
          }

          const nextOrderIdMap = {
            ...(existing.orderIdMap || {}),
          } as Record<string, string>;
          if (orderId !== undefined) {
            nextOrderIdMap[orderNo] = orderId;
          }

          const nextOrderDataMap = {
            ...(existing.orderDataMap || {}),
          } as Record<string, any>;
          if (orderData !== undefined) {
            nextOrderDataMap[orderNo] = orderData;
          }

          const next = { ...state.selectionsByGroup } as Record<
            string,
            GroupSelection
          >;
          next[groupKey] = {
            ...existing,
            selectedOrders: nextSelectedOrders,
            orderDeliveryModes: nextOrderDeliveryModes,
            orderIdMap: nextOrderIdMap,
            orderDataMap: nextOrderDataMap,
          };

          if (
            state.allowMultiCustomerShipment &&
            deliveryMode === DeliveryModes.DOOR_DELIVERY &&
            orderData?.cptSlab?.slabLevel !== -2
          ) {
            const consolidatedKey =
              getConsolidatedSupplierGroupKey(safeSupplierId);
            const consolidatedExisting =
              next[consolidatedKey] ||
              buildEmptyGroup(
                customerId,
                "Multiple Customers",
                safeSupplierId,
                safeSupplierName,
                true
              );

            const consolidatedOrderSet = new Set(
              consolidatedExisting.selectedOrders
            );
            consolidatedOrderSet.add(orderNo);

            const consolidatedDeliveryModes = {
              ...(consolidatedExisting.orderDeliveryModes || {}),
              [orderNo]: DeliveryModes.DOOR_DELIVERY,
            };
            const consolidatedConsigneeNames = {
              ...(consolidatedExisting.consigneeNames || {}),
              [orderNo]: customerName,
            };
            const consolidatedOrderIdMap = {
              ...(consolidatedExisting.orderIdMap || {}),
            };
            if (orderId !== undefined) {
              consolidatedOrderIdMap[orderNo] = orderId;
            }
            const consolidatedOrderDataMap = {
              ...(consolidatedExisting.orderDataMap || {}),
            };
            if (orderData !== undefined) {
              consolidatedOrderDataMap[orderNo] = orderData;
            }

            next[consolidatedKey] = {
              ...consolidatedExisting,
              selectedOrders: Array.from(consolidatedOrderSet),
              orderDeliveryModes: consolidatedDeliveryModes,
              consigneeNames: consolidatedConsigneeNames,
              orderIdMap: consolidatedOrderIdMap,
              orderDataMap: consolidatedOrderDataMap,
              isConsolidatedGroup: true,
            };
          }

          return {
            selectedOrders: Array.from(
              new Set([...state.selectedOrders, orderNo])
            ),
            selectionsByGroup: next,
          };
        });
      },

      removeSelectedOrder: (
        orderNo,
        customerId,
        customerName,
        supplierId,
        supplierName
      ) => {
        const safeSupplierId = supplierId || "no-supplier";
        const groupKey = getCustomerSupplierGroupKey(
          customerId,
          safeSupplierId
        );

        set((state) => {
          const existing = state.selectionsByGroup[groupKey];
          if (!existing) return state;

          const existingSet = new Set(existing.selectedOrders);
          existingSet.delete(orderNo);
          const nextSelectedOrders = Array.from(existingSet);

          const nextOrderDeliveryModes = {
            ...(existing.orderDeliveryModes || {}),
          } as Record<string, DeliveryModes>;
          delete nextOrderDeliveryModes[orderNo];

          const nextOrderIdMap = { ...(existing.orderIdMap || {}) };
          delete nextOrderIdMap[orderNo];

          const nextOrderDataMap = { ...(existing.orderDataMap || {}) };
          delete nextOrderDataMap[orderNo];

          const next = { ...state.selectionsByGroup } as Record<
            string,
            GroupSelection
          >;
          if (nextSelectedOrders.length === 0) {
            delete next[groupKey];
          } else {
            next[groupKey] = {
              ...existing,
              selectedOrders: nextSelectedOrders,
              orderDeliveryModes: nextOrderDeliveryModes,
              orderIdMap: nextOrderIdMap,
              orderDataMap: nextOrderDataMap,
            };
          }

          if (state.allowMultiCustomerShipment) {
            const consolidatedKey =
              getConsolidatedSupplierGroupKey(safeSupplierId);
            const consolidatedExisting = next[consolidatedKey];
            if (!consolidatedExisting) {
              return {
                selectedOrders: state.selectedOrders.filter(
                  (id) => id !== orderNo,
                ),
                selectionsByGroup: next,
              };
            }

            const consolidatedOrderSet = new Set(
              consolidatedExisting.selectedOrders
            );
            consolidatedOrderSet.delete(orderNo);
            const consolidatedOrders = Array.from(consolidatedOrderSet);

            const consolidatedDeliveryModes = {
              ...(consolidatedExisting.orderDeliveryModes || {}),
            };
            delete consolidatedDeliveryModes[orderNo];

            const consolidatedConsigneeNames = {
              ...(consolidatedExisting.consigneeNames || {}),
            };
            delete consolidatedConsigneeNames[orderNo];

            const consolidatedOrderIdMap = {
              ...(consolidatedExisting.orderIdMap || {}),
            };
            delete consolidatedOrderIdMap[orderNo];

            const consolidatedOrderDataMap = {
              ...(consolidatedExisting.orderDataMap || {}),
            };
            delete consolidatedOrderDataMap[orderNo];

            if (consolidatedOrders.length === 0) {
              delete next[consolidatedKey];
            } else {
              next[consolidatedKey] = {
                ...consolidatedExisting,
                selectedOrders: consolidatedOrders,
                orderDeliveryModes: consolidatedDeliveryModes,
                consigneeNames: consolidatedConsigneeNames,
                orderIdMap: consolidatedOrderIdMap,
                orderDataMap: consolidatedOrderDataMap,
              };
            }
          }

          return {
            selectedOrders: state.selectedOrders.filter((id) => id !== orderNo),
            selectionsByGroup: next,
          };
        });
      },

      clearGroup: (groupKey) => {
        set((state) => {
          const group = state.selectionsByGroup[groupKey];
          if (!group) return state;

          const ordersToRemove = new Set(group.selectedOrders);
          const next = { ...state.selectionsByGroup } as Record<
            string,
            GroupSelection
          >;

          for (const [key, otherGroup] of Object.entries(next)) {
            const remaining = otherGroup.selectedOrders.filter(
              (orderNo) => !ordersToRemove.has(orderNo)
            );

            if (remaining.length === 0) {
              delete next[key];
              continue;
            }

            if (remaining.length !== otherGroup.selectedOrders.length) {
              const orderDeliveryModes = { ...otherGroup.orderDeliveryModes };
              const consigneeNames = { ...(otherGroup.consigneeNames || {}) };
              const orderIdMap = { ...(otherGroup.orderIdMap || {}) };
              const orderDataMap = { ...(otherGroup.orderDataMap || {}) };
              for (const orderNo of otherGroup.selectedOrders) {
                if (ordersToRemove.has(orderNo)) {
                  delete orderDeliveryModes[orderNo];
                  delete consigneeNames[orderNo];
                  delete orderIdMap[orderNo];
                  delete orderDataMap[orderNo];
                }
              }
              next[key] = {
                ...otherGroup,
                selectedOrders: remaining,
                orderDeliveryModes,
                consigneeNames,
                orderIdMap,
                orderDataMap,
              };
            }
          }

          return {
            selectedOrders: state.selectedOrders.filter(
              (id) => !ordersToRemove.has(id)
            ),
            selectionsByGroup: next,
          };
        });
      },

      clearAllSelections: () =>
        set({ selectedOrders: [], selectionsByGroup: {} }),

      updateGroupQuote: (groupKey, updates) => {
        set((state) => {
          const existing = state.selectionsByGroup[groupKey];
          if (!existing) return state;

          const next = { ...state.selectionsByGroup } as Record<
            string,
            GroupSelection
          >;
          next[groupKey] = { ...existing, ...updates } as GroupSelection;

          return { selectionsByGroup: next };
        });
      },

      setGroupLoading: (groupKey, loading) => {
        set((state) => {
          const existing = state.selectionsByGroup[groupKey];
          if (!existing) return state;

          const next = { ...state.selectionsByGroup } as Record<
            string,
            GroupSelection
          >;
          next[groupKey] = {
            ...existing,
            porterQuoteLoading: loading,
          } as GroupSelection;

          return { selectionsByGroup: next };
        });
      },
    }),
    {
      name: "r2d-orders-storage",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        selectedOrders: state.selectedOrders,
        selectionsByGroup: state.selectionsByGroup,
      }),
      onRehydrateStorage: () => (state) => {
        if (!state?.allowMultiCustomerShipment) {
          state.selectionsByGroup = stripConsolidatedSelectionGroups(
            state.selectionsByGroup ?? {},
          );
        }
      },
    }
  )
);
